/**
 * MODULE: platform/browser/frontend/components/AssistantRunInBrowserButton.tsx
 * PURPOSE: Shell assistant dock — hand off CRM context to AI Browser automation.
 */

import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { runBrowserLaunchFromAssistant } from '../utils/launchBrowserFromAssistant';

export type AssistantRunInBrowserButtonProps = {
  prefillContext?: string | null;
  pagePath: string;
  pageTitle?: string;
  suggestedPrompts?: string[];
  instruction?: string;
  onBeforeNavigate?: () => void;
  dataTestId?: string;
  compact?: boolean;
};

export function AssistantRunInBrowserButton({
  prefillContext,
  pagePath,
  pageTitle,
  suggestedPrompts = [],
  instruction,
  onBeforeNavigate,
  dataTestId = 'assistant-run-in-browser',
  compact = false,
}: AssistantRunInBrowserButtonProps) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      data-testid={dataTestId}
      title="Run this task in AI Browser"
      onClick={() =>
        runBrowserLaunchFromAssistant(navigate, {
          instruction,
          prefillContext: prefillContext ?? undefined,
          pagePath,
          pageTitle,
          suggestedPrompts,
          onBeforeNavigate,
        })
      }
      className={
        compact
          ? 'inline-flex shrink-0 items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-900 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100 dark:hover:bg-sky-900/50'
          : 'inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-900 shadow-sm transition hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100 dark:hover:bg-sky-900/50'
      }
    >
      <GlobeAltIcon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
      {compact ? 'Browser' : 'Automate in Browser'}
    </button>
  );
}
